import { ReactNode, useState } from "react";
import { useSnackbar } from "notistack";
import {
  Box,
  Grid,
  IconButton,
  SxProps,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { Flex } from "~/components/Flex";
import { useSearchParams } from "react-router-dom";
import {
  LoadResourceInvitationsDocument,
  LoadResourceInvitationsQuery,
  useCancelInvitationMutation,
  useLoadResourceInvitationsQuery,
  useResendInvitationMutation,
} from "~/operations";
import { LoadingFailedPage, LoadingPage } from "~/components/loading";
import {
  Delete as DeleteIcon,
  Replay as ReplayIcon,
} from "@mui/icons-material";
import { DataTable } from "~/components/data-table";
import { ConfirmationDialog } from "~/components/ConfirmationDialog";
import { SectionHeading } from "~/components/DetailsPage/components";
import { RoleCell } from "~/components/Members/RoleCell";

type Invitation = NonNullable<
  NonNullable<
    NonNullable<LoadResourceInvitationsQuery["invitations"]>["edges"]
  >[0]["node"]
>;

interface ResourceInvitationsProps {
  mrn: string;
}

export function ResourceInvitations({ mrn }: ResourceInvitationsProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams] = useSearchParams();
  const [inviteeToCancel, setInviteeToCancel] = useState<Invitation | null>(
    null,
  );

  const { loading, data, error } = useLoadResourceInvitationsQuery({
    variables: { resourceMrn: mrn },
  });

  const [cancelInvitation, cancelResponse] = useCancelInvitationMutation({
    refetchQueries: [LoadResourceInvitationsDocument],
  });

  const [resendInvitation] = useResendInvitationMutation({
    refetchQueries: [LoadResourceInvitationsDocument],
  });

  if (loading) {
    return <LoadingPage what="invitations" />;
  }

  if (error || !data?.invitations) {
    return <LoadingFailedPage what="invitations" />;
  }

  const query = searchParams.get("query") || "";

  const allInvitations =
    data.invitations.edges?.flatMap((e) => (e.node ? e.node : [])) || [];

  const filteredInvitations = allInvitations.filter(
    ({ senderEmail, inviteeEmail }) => {
      if (!query) return true;
      const filterable = [senderEmail, inviteeEmail];
      return filterable.some((value) =>
        value.toLowerCase().includes(query.toLowerCase()),
      );
    },
  );

  if (allInvitations.length === 0) {
    return null;
  }

  const onCancelInvitation = async (invite: Invitation) => {
    try {
      await cancelInvitation({ variables: { invitationMrn: invite.mrn } });
    } catch {
      let noPermission =
        cancelResponse.error?.message.includes("PermissionDenied");

      if (noPermission) {
        enqueueSnackbar(
          "You do not have permission to delete invitations you don't own.",
          { variant: "error" },
        );

        return;
      }

      if (cancelResponse.called) {
        enqueueSnackbar("Ran into problem when canceling invitation.", {
          variant: "error",
        });
      }
    }
  };

  const handleResendInvitation = async (invite: Invitation) => {
    try {
      await resendInvitation({ variables: { invitationMrn: invite.mrn } });
      enqueueSnackbar("Invitation successfully resent", {
        variant: "success",
      });
    } catch {
      enqueueSnackbar("Failed to resend invitation", {
        variant: "error",
      });
    }
  };

  const handleCancelInvitation = (invite: Invitation) => {
    setInviteeToCancel(invite);
  };

  const handleConfirmCancelInvitation = async () => {
    if (inviteeToCancel) {
      await onCancelInvitation(inviteeToCancel);
      setInviteeToCancel(null);
    }
  };

  const handleCloseCancelInvitationDialog = () => {
    setInviteeToCancel(null);
  };

  return (
    <Grid size={12}>
      <SectionHeading
        heading="Pending Invitations"
        sx={{ marginTop: 0, marginBottom: 2 }}
      />
      <Box>
        <DataTable id="invitation-list">
          <TableHead>
            <TableRow>
              {tableHeaders.map((header) => (
                <TableCell key={header.id} sx={{ ...header.options }}>
                  {header.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredInvitations.map((invite) => (
              <TableRow key={invite.mrn}>
                <TableCell>{invite.inviteeEmail}</TableCell>
                <RoleCell roles={invite.roleMrns || []} />
                <TableCell>{invite.senderEmail}</TableCell>
                <TableCell>
                  <Flex justifyContent="flex-end">
                    <Tooltip title="Re-send invitation">
                      <IconButton
                        aria-label="Re-send invitation"
                        onClick={() => handleResendInvitation(invite)}
                      >
                        <ReplayIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete invitation">
                      <IconButton
                        aria-label="Delete invitation"
                        onClick={() => handleCancelInvitation(invite)}
                      >
                        <DeleteIcon color="error" />
                      </IconButton>
                    </Tooltip>
                  </Flex>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </DataTable>

        {/* Confirmation Dialog */}
        <ConfirmationDialog
          isOpen={Boolean(inviteeToCancel)}
          onClose={handleCloseCancelInvitationDialog}
          onConfirm={handleConfirmCancelInvitation}
          title="Delete Invitation"
          confirmButtonText="Delete"
          content={
            <Typography>
              Are you sure you want to delete the invitation for{" "}
              <strong>{inviteeToCancel?.inviteeEmail}</strong>?
              <br />
              You can always invite them again at a later time.
            </Typography>
          }
        />
      </Box>
    </Grid>
  );
}

type Header = {
  id: string;
  label: ReactNode;
  colSpan?: number;
  sortable?: boolean;
  options?: SxProps;
};

const tableHeaders: Header[] = [
  {
    id: "invited",
    label: "Invited",
  },
  { id: "ROLE", label: "Role" },
  {
    id: "invited_by",
    label: "Invited by",
  },
  {
    id: "ACTIONS",
    label: undefined,
  },
];
